import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { api, can, publicApiUrl, type EndpointDetail, type EndpointReport } from '../api'
import { ErrorBox, FindingsList, Pill } from '../components'
import { dt, num } from '../format'
import { normalizeSpec, type Spec } from '../endpointSpec'
import { EndpointForm, EndpointPreview } from './EndpointForm'
import { GateBadge } from './Endpoints'
import { HistoryLink } from './history'

export function EndpointDetailPage() {
  const { slug = '' } = useParams()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({ queryKey: ['endpoint', slug], queryFn: () => api.endpoint(slug) })
  const onSaved = (d: EndpointDetail) => {
    qc.setQueryData(['endpoint', slug], d)
    qc.invalidateQueries({ queryKey: ['endpoints'] })
  }
  const publish = useMutation({ mutationFn: () => api.publishEndpoint(slug), onSuccess: onSaved })
  const unpublish = useMutation({ mutationFn: () => api.unpublishEndpoint(slug), onSuccess: onSaved })

  if (q.error) return <p className="callout err">{q.error.message}</p>
  if (!q.data) return <p className="muted">Loading…</p>
  const e = q.data
  const admin = can(me.data, 'admin')
  const live = e.status === 'published'
  const tryUrl = `${publicApiUrl}${e.gate.example || e.path}`
  const maxUsage = Math.max(1, ...e.usage.map((u) => u.keyed + u.anonymous))

  return (
    <section className="page">
      <p className="crumbs">
        <Link to="/endpoints">Endpoints</Link> / <span className="mono">{e.slug}</span>
      </p>
      <header className="page-h">
        <div>
          <h1 className="mono">GET {e.path}</h1>
          <p className="sub">
            {e.title} · bucket <Link to={`/buckets/${e.bucket}`}>{e.bucket}</Link>
          </p>
        </div>
        <div className="row">
          <HistoryLink kind="endpoint" slug={e.slug} />
          {live ? <Pill kind="ok">live v{e.published_version}</Pill> : <Pill kind="warn">{e.status}</Pill>}
          <GateBadge e={e} />
          {admin && (
            <button
              className="btn btn-primary"
              disabled={!e.gate.ok || publish.isPending || (live && e.published_version === e.current_version)}
              title={e.gate.ok ? `Serve v${e.current_version} on the API` : 'The gate has blocking findings'}
              onClick={() => publish.mutate()}
            >
              Publish v{e.current_version}
            </button>
          )}
          {admin && live && (
            <button className="btn" disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>
              Unpublish
            </button>
          )}
        </div>
      </header>
      {publish.error && <ErrorBox error={publish.error} />}
      {unpublish.error && <ErrorBox error={unpublish.error} />}
      {live && e.published_path !== e.path && <p className="callout warn">Live v{e.published_version} serves {e.published_path}; publish to switch.</p>}

      <div className="cols-2">
        <div className="card">
          <h2>Publish gate (v{e.current_version})</h2>
          {e.gate.problems.map((p, i) => (
            <p key={i} className="callout err">
              <strong>Problem</strong> {p}
            </p>
          ))}
          <FindingsList findings={e.gate.findings} empty="Every check passes; an admin can publish." />
          {e.gate.effective && (
            <dl className="kv">
              <dt>Licences</dt>
              <dd>{e.gate.effective.licenses.join(' + ')}</dd>
              <dt>Usage mode</dt>
              <dd className="mono">{e.gate.effective.usage_mode}</dd>
              <dt>Attribution</dt>
              <dd>{e.gate.effective.attribution.join(' ')}</dd>
            </dl>
          )}
        </div>
        <div className="card">
          <h2>Try it</h2>
          {live ? (
            <>
              <code className="block">curl -s '{tryUrl}'</code>
              <p>
                <a href={tryUrl} target="_blank" rel="noreferrer">
                  Open in a new tab
                </a>{' '}
                · <a href={`${publicApiUrl}/openapi.json`}>OpenAPI</a>
              </p>
            </>
          ) : (
            <p className="muted">Not published. Example request once live: <code>{e.gate.example}</code></p>
          )}
          <h2>Requests, last 14 days</h2>
          <table className="tbl">
            <thead>
              <tr><th>Day</th><th className="num">With key</th><th className="num">Anonymous</th><th /></tr>
            </thead>
            <tbody>
              {e.usage.map((u) => (
                <tr key={u.day}>
                  <td className="mono">{u.day.slice(0, 10)}</td>
                  <td className="num">{num(u.keyed)}</td>
                  <td className="num">{num(u.anonymous)}</td>
                  <td style={{ width: '40%' }}>
                    <span className="bar" style={{ width: `${(100 * (u.keyed + u.anonymous)) / maxUsage}%` }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <McpCard e={e} admin={admin} onSaved={onSaved} />

      <EndpointSpecEditor key={e.current_version} e={e} editor={can(me.data, 'editor')} onSaved={onSaved} />
    </section>
  )
}

function EndpointSpecEditor({ e, editor, onSaved }: { e: EndpointDetail; editor: boolean; onSaved: (d: EndpointDetail) => void }) {
  const current = e.versions[0]
  const [version, setVersion] = useState(current?.version ?? 0)
  const shown = e.versions.find((v) => v.version === version) ?? current
  const [spec, setSpec] = useState<Spec>(() => (current?.body as Spec) ?? {})
  const [comment, setComment] = useState('')
  const [report, setReport] = useState<EndpointReport | null>(null)
  const validate = useMutation({ mutationFn: (s: Spec) => api.validateEndpoint(e.slug, s), onSuccess: setReport })
  const save = useMutation({ mutationFn: (s: Spec) => api.saveEndpoint(e.slug, s, comment), onSuccess: onSaved })
  const dirty = JSON.stringify(normalizeSpec(spec)) !== JSON.stringify(normalizeSpec((current?.body as Spec) ?? {}))
  const old = version !== current?.version

  return (
    <>
      <div className="card">
        <div className="row-between">
          <h2>Endpoint spec</h2>
          <select aria-label="Version" value={version} onChange={(ev) => setVersion(Number(ev.target.value))}>
            {e.versions.map((v) => (
              <option key={v.version} value={v.version}>
                v{v.version}
                {v.version === e.published_version ? ' (live)' : ''} · {dt(v.created_at)} · {v.created_by}
                {v.comment ? ` · ${v.comment}` : ''}
              </option>
            ))}
          </select>
        </div>
        {old && shown ? (
          <>
            <p className="note">An earlier version, read-only. Choose the latest to edit.</p>
            <code className="block spec">{JSON.stringify(shown.body, null, 2)}</code>
          </>
        ) : (
          <SpecEditor spec={spec} onChange={(s) => {
            setSpec(s)
            setReport(null)
          }} readOnly={!editor} />
        )}
        {!old && (
          <div className="row">
            <button className="btn" disabled={validate.isPending} onClick={() => validate.mutate(normalizeSpec(spec))}>
              Validate
            </button>
            {editor && (
              <>
                <input className="grow" placeholder="What changed" value={comment} onChange={(ev) => setComment(ev.target.value)} />
                <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(normalizeSpec(spec))}>
                  Save as v{e.current_version + 1}
                </button>
              </>
            )}
          </div>
        )}
        {validate.error && <ErrorBox error={validate.error} />}
        {save.error && <ErrorBox error={save.error} />}
        {report && <SpecReportView report={report} />}
        <p className="note">Saving creates a new version; it goes live on dev or prod only when an admin switches it on there.</p>
      </div>
      {!old && <EndpointPreview slug={e.slug} spec={spec} />}
    </>
  )
}

/** The form, or the raw JSON for anything the form does not cover. */
export function SpecEditor({ spec, onChange, readOnly }: { spec: Spec; onChange: (s: Spec) => void; readOnly: boolean }) {
  const [tab, setTab] = useState<'form' | 'json'>('form')
  const [text, setText] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  return (
    <>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'form'} className={tab === 'form' ? 'on' : ''} onClick={() => setTab('form')}>
          Form
        </button>
        <button role="tab" aria-selected={tab === 'json'} className={tab === 'json' ? 'on' : ''} onClick={() => {
          setText(JSON.stringify(normalizeSpec(spec), null, 2))
          setParseError(null)
          setTab('json')
        }}>
          JSON
        </button>
      </div>
      {tab === 'form' ? (
        <EndpointForm spec={spec} onChange={onChange} readOnly={readOnly} />
      ) : (
        <>
          <textarea className="spec mono" spellCheck={false} rows={22} value={text} readOnly={readOnly}
            onChange={(ev) => {
              setText(ev.target.value)
              try {
                onChange(JSON.parse(ev.target.value))
                setParseError(null)
              } catch (err) {
                setParseError((err as Error).message)
              }
            }} />
          {parseError && <p className="callout err">Not valid JSON: {parseError}</p>}
        </>
      )}
    </>
  )
}

export function SpecReportView({ report }: { report: EndpointReport }) {
  return (
    <div className="report">
      {report.problems.map((p, i) => (
        <p key={i} className="callout err">
          <strong>Problem</strong> {p}
        </p>
      ))}
      <FindingsList findings={report.findings} />
      {report.problems.length === 0 && (
        <p className="callout ok">
          The spec fits its bucket{report.ok ? ' and passes the publish gate' : '; the gate findings above block publishing'}. Example:{' '}
          <code>{report.example}</code>
        </p>
      )}
    </div>
  )
}

/** The endpoint as an MCP tool (milestone 13): the switch and the tool
 * that AI assistants see. */
function McpCard({ e, admin, onSaved }: { e: EndpointDetail; admin: boolean; onSaved: (d: EndpointDetail) => void }) {
  const spec = e.versions[0]?.body
  const preview = useQuery({ queryKey: ['mcp-preview', e.slug, e.current_version], queryFn: () => api.mcpPreview(e.slug, spec), enabled: !!spec })
  const toggle = useMutation({ mutationFn: (on: boolean) => api.setEndpointMCP(e.slug, on), onSuccess: onSaved })
  const mcpUrl = `${publicApiUrl}${e.org_id === 'aleva' ? '' : `/v1/o/${e.org_id}`}/mcp`
  return (
    <div className="card">
      <div className="row-between">
        <h2>MCP tool</h2>
        <label className="check" title={admin ? '' : 'Admins switch this'}>
          <input type="checkbox" role="switch" checked={e.mcp_enabled} disabled={!admin || toggle.isPending || preview.data?.bulk}
            onChange={(ev) => toggle.mutate(ev.target.checked)} />
          Available over MCP
        </label>
      </div>
      {toggle.error && <ErrorBox error={toggle.error} />}
      <p className="note">
        While published, AI assistants connected to <code>{mcpUrl}</code> can call this endpoint as a tool, with the same limits and
        API keys as the REST API. {preview.data?.bulk && 'Bulk endpoints are never tools.'} Override the name, description and
        parameter texts in the spec's <code>mcp</code> block (JSON tab below).
      </p>
      {preview.error && <ErrorBox error={preview.error} />}
      {preview.data && (
        <dl className="kv">
          <dt>Name</dt>
          <dd className="mono">{preview.data.tool.name}</dd>
          <dt>Description</dt>
          <dd>{preview.data.tool.description}</dd>
          <dt>Input</dt>
          <dd>
            <pre className="mono note">{JSON.stringify(preview.data.tool.inputSchema, null, 2)}</pre>
          </dd>
        </dl>
      )}
    </div>
  )
}
